import { OfferDataInterface, PaymentMethodOffer, PaymentOfferRule } from "./@types";
import format from "date-fns/format";
import { DATE_FORMATS } from "constants/index";
import { toLowerCase, toLowerCaseWithOptionalParam } from "./toLowerCase";
import { Font, Theme } from "@nykaa/ui-components/themes/types";
import { DEVICE_TYPE } from "constants/index";
import { getIcons, ICONS_NAMES } from "helpers/utils/getIcons";
import { isMobileApp } from "helpers/deviceType";
import { formatNumber } from "helpers/utils/numberFormatter";

/**
 * @description
 * get the offer keys from cart offers api.
 * @returns {object} returns the object on the based on paymentMethod and ruleKey.
 */
export const getOfferKeys = (offersData: PaymentMethodOffer) => {
  const paymentRules = offersData?.paymentRules || [];
  const nonEligiblePaymentRules = offersData?.nonEligiblePaymentRules || [];
  const allOffers = [...paymentRules, ...nonEligiblePaymentRules];
  /* eslint-disable @typescript-eslint/no-object-literal-type-assertion */
  let offerKeys = {} as PaymentOfferRule;
  allOffers.forEach((paymentRule: PaymentOfferRule) => {
    if (paymentRule?.paymentMethod && paymentRule?.ruleKey) {
      if (
        offerKeys[paymentRule?.paymentMethod] &&
        !offerKeys[paymentRule?.paymentMethod].includes(paymentRule?.ruleKey)
      ) {
        offerKeys[paymentRule?.paymentMethod].push(paymentRule?.ruleKey);
      } else {
        offerKeys[paymentRule?.paymentMethod] = [paymentRule?.ruleKey];
      }
    }
  });
  return offerKeys;
};

export const getFormattedDate = (dateTobeFormatted: Date, formatNeeded: string = "") => {
  if (formatNeeded) {
    try {
      // Format Date
      return format(dateTobeFormatted, formatNeeded);
    } catch (error) {
      // Couldn't format the given date into required formatNeeded
      throw error;
    }
  }
  // Return the original dateTobeFormatted as we couldn't convert it into requested formatNeeded
  return dateTobeFormatted;
};

export const getOfferKeyList = (paymentRules: PaymentOfferRule[], paymentMethod: string) => {
  let offerData = paymentRules.filter(
    (paymentRule: PaymentOfferRule) =>
      toLowerCaseWithOptionalParam(paymentRule?.paymentMethod) === toLowerCase(paymentMethod)
  );
  /* eslint-disable no-shadow */
  const uniqOfferData: any = new Set(offerData?.map((offerData: any) => offerData?.ruleKey));
  return Array.from(uniqOfferData);
};

/**
 * @description
 * generates message on offer
 * based on discountPercent or discountAmount
 * @param offersData - offers object from offersContext
 * @returns offerMessage
 */
export const getOfferMessage = (offersData: OfferDataInterface) => {
  let offerMessage = "";
  if (offersData?.hasOffers) {
    if (offersData.discountAmount && offersData.discountAmount > 0) {
      offerMessage = `₹${formatNumber(offersData.discountAmount)} OFF`;
    } else if (offersData.discountPercent && offersData.discountPercent > 0) {
      offerMessage = `${offersData.discountPercent}% OFF`;
    }
  }
  return offerMessage;
};

export const getOfferMessages = (
  offersData: OfferDataInterface,
  theme: Theme,
  action: (e: Event) => void
) => {
  const offers = [];
  let offerMessages = [];
  let offerText;
  if (offersData?.discountAmountMsg) {
    offerMessages = offersData?.discountAmountMsg.split("-");
    if (offerMessages?.length > 1) {
      offerText = `<b style='font-weight: 600;letter-spacing: 0.9px;'>${offerMessages?.[0]}</b> - ${offerMessages?.[1]}`;
    } else {
      offerText = offersData?.discountAmountMsg || "";
    }
  }
  if (offersData?.hasOffers) {
    offers.push({
      text: offerText,
      success: true,
      endIcon: getIcons("", ICONS_NAMES.SUCCESS_INFO),
      color: theme.colors.positive,
      font: theme.typography.bodySmall,
      action: action,
    });
  }
  return offers;
};

/* eslint-disable @typescript-eslint/class-name-casing */
export interface getPaymentOfferInfoConfigInterface {
  backIcon?: string;
  icon?: string;
  title?: string;
  titleFont?: Font;
  subTitle?: string;
  subTitleFont?: Font;
  priceList?: {
    label: string;
    value: string;
  }[];
  totalText?: {
    label: string;
    value: string;
  };
  tncList?: string[];
  tncButtonText?: string;
  tncButtonAction?: () => void;
  theme?: Theme;
  gotItButton?: boolean;
  scrollHeightVisible?: boolean;
  isRightSideBar?: boolean;
  sideBarVisible?: boolean;
  width?: string;
  handleBackButton?: () => void;
  bottomButtonText?: string;
  bottomButtonAction?: () => void;
  bottomSheetVisible?: boolean;
  bottomSheetClose?: () => void;
  overlayClick?: () => void;
}

export const getPaymentOfferInfoConfig = ({
  offerData,
  theme,
  action,
  deviceType,
  isVisible,
}: any) => {
  const isDesktop = deviceType === DEVICE_TYPE.DESKTOP;
  let config: getPaymentOfferInfoConfigInterface = {
    backIcon: "https://images-static.nykaa.com/media/wysiwyg/Payments/circle-x-duotone.svg",
    icon: offerData?.offerImageUrl,
    title: offerData?.name,
    titleFont: theme.typography.type202,
    subTitle: offerData?.offerLongDescription,
    subTitleFont: theme.typography.type140,
    priceList: [
      {
        label: offerData?.minPayableAmount > 0 ? "Min. order value" : "",
        value: offerData?.minPayableAmount > 0 ? `₹${formatNumber(offerData?.minPayableAmount)}` : "",
      },
      {
        label: offerData?.maximumDiscount > 0 ? "Max. discount/cashback" : "",
        value: offerData?.maximumDiscount > 0 ? `₹${formatNumber(offerData?.maximumDiscount)}` : "",
      },
      {
        label: offerData?.usagePerCustomer ? "Max. Usage per account" : "",
        value: offerData?.usagePerCustomer ? offerData?.usagePerCustomer : "",
      },
      {
        label: offerData?.validTillDate ? "Valid Till" : "",
        value: offerData?.validTillDate
          ? offerData?.validTillDate &&
            getFormattedDate(new Date(offerData.validTillDate), DATE_FORMATS.PAYMENT_OFFER)
          : "",
      },
    ],
    totalText: {
      label: offerData?.discountAmount > 0 ? "You get" : "",
      value: offerData?.discountAmount > 0 ? `₹${formatNumber(Math.floor(offerData?.discountAmount))}` : "",
    },
    tncList: offerData?.offerTNC || [],
    tncButtonText: "terms & conditions",
    tncButtonAction: () => {
      if (isMobileApp()) {
        window.open(offerData?.termsAndConditionsLink);
      } else {
        window.open(offerData?.termsAndConditionsLink, "_blank");
      }
    },
    theme,
    gotItButton: false,
    scrollHeightVisible: false,
  };

  if (isDesktop) {
    config.isRightSideBar = true;
    config.sideBarVisible = isVisible;
    config.width = "416px";
    config.handleBackButton = action;
    config.bottomButtonText = "Got it";
    config.bottomButtonAction = action;
    config.gotItButton = true;
    config.scrollHeightVisible = true;
  } else {
    config.bottomSheetVisible = isVisible;
    config.bottomSheetClose = action;
    config.overlayClick = action;
    config.bottomButtonText = "Got it";
    config.bottomButtonAction = action;
  }
  return config;
};

export const get: any = (obj: any, key: any, fallBack: any) => {
  try {
    const dot = key?.indexOf(".");

    if (obj === undefined) {
      return fallBack || undefined;
    }

    if (dot === -1) {
      if (key?.length && key in obj) {
        return obj[key];
      }

      return fallBack || undefined;
    }

    return get(obj[key?.substr(0, dot)], key?.substr(dot + 1), fallBack);
  } catch (error) {
    return null;
  }
};
